import { useCallback, useMemo, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import {
  Button,
  Container,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  Grid,
  Header,
  Input,
  Pagination,
  Popup,
  Table,
  TabPane,
} from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"
import { useThrottle } from "simplydo/react/hooks"
import { styled } from "styled-components"

import actions from "actions"
import api, { asQuery } from "api"
import { UserChooser } from "components/lib/Choosers"
import { AssessorAudienceWarning } from "components/lib/Choosers/UserChooser/Content/ChallengeAudienceWarning"
import ChallengeIdeaFilters, { ChallengeFilterKeys } from "components/lib/Filters/ChallengeIdeaFilters"
import { HandledIdeaPreview } from "components/lib/Ideas"
import { type ChallengesReducer, defaultInitialState } from "reducers/challenges"
import { useAppDispatch, useAppSelector } from "store"
import Prompt from "utils/Prompt"
import util from "utils/utils"

import { AssessmentIdeaRow } from "./AssessmentIdeaRow"
import { AssessorRow } from "./AssessorRow"
import { AssessmentReviewModal } from "./AssignmentReviewModal"
import { IdeaInspectModal } from "./IdeaInspectModal"
import { UserInspectModal } from "./UserInspectModal"

export const TableHeaderItem = styled(Header)`
  img {
    height: 26px !important;
    width: 26px !important;
    margin-right: 8px !important;
  }
  div.image {
    margin-right: 5px !important;
  }
`

const GridHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  > .header {
    margin: 0;
  }
`

export type AssignableUser = OpenAPI.GET<"/challenges/{id}/assessments/dashboard">["response"]["emails"][0] &
  OpenAPI.GET<"/challenges/{id}/assessments/dashboard">["response"]["users"][0]

export type LocalAssignments = {
  [userId: string]: {
    user: AssignableUser
    ideas: {
      [key: string]: {
        idea: OpenAPI.Schemas["Idea"]
        assessmentRoundIds: string[]
      }
    }
  }
}

type AssignmentDashboardProps = {
  challenge: OpenAPI.GET<"/challenges/{id}">["response"]
}

const AssignmentDashboard = ({ challenge }: AssignmentDashboardProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.user)

  const template = challenge?.ownerIdeaTemplate
  const assessmentRounds = template?.assessments?.rounds || [{ id: "legacy", name: "Assessment" }]
  const assessmentRoundMap = useMemo(() => {
    return Object.fromEntries(assessmentRounds.map((r) => [r.id, r]))
  }, [assessmentRounds])

  const challengesReducer: ChallengesReducer = useAppSelector((state) => state.challenges)
  const {
    filtersVisible,
    tagFilter,
    ideaIncludes: filter,
    ideaOrder: sort,
    ideaOrderDirection: sortDirection,
    labelsFilter: labels,
    ideaFieldChoices: fieldChoices,
    projectBoardLanesFilter,
    groupFilter,
    orgFilter,
    projectBoardFilter,
    adminHasCommented,
    impactFilter,
  } = challengesReducer

  const numberOfFiltersNotDefault = useMemo(
    () =>
      ChallengeFilterKeys.filter((key) => {
        return !util.variablesAreEqual(challengesReducer[key], defaultInitialState[key])
      }).length,
    [challengesReducer],
  )
  const updateFilters = useCallback((data) => dispatch(actions.challenges.updateIdeaFilters(data)), [dispatch])

  const [reviewOpen, setReviewOpen] = useState(false)
  const [previewIdea, setPreviewIdea] = useState<string | null>(null)

  // Local state for tracking draft USER assignments made before saving
  const [localUserAssignments, setLocalUserAssignments] = useState<LocalAssignments>({})
  const localUserAssignmentList = useMemo(() => Object.entries(localUserAssignments), [localUserAssignments])

  // Local state for tracking draft EMAIL assignments made before saving
  const [localEmailAssignments, setLocalEmailAssignments] = useState<LocalAssignments>({})
  const localEmailAssignmentList = useMemo(() => Object.entries(localEmailAssignments), [localEmailAssignments])

  // The count of assignments in draft to display in the UI
  const totalAssignmentCount = useMemo(() => {
    let assignmentCount = 0
    localUserAssignmentList.forEach(([userId, assignment]) => {
      Object.values(assignment.ideas).forEach((assign) => {
        assignmentCount += assign.assessmentRoundIds.length
      })
    })
    localEmailAssignmentList.forEach(([userId, assignment]) => {
      Object.values(assignment.ideas).forEach((assign) => {
        assignmentCount += assign.assessmentRoundIds.length
      })
    })
    return assignmentCount
  }, [localUserAssignmentList, localEmailAssignmentList])

  // The state of which users/emails/ideas are selected in the UI before being assigned to the draft states above
  const [selectedUsers, setSelectedUsers] = useState<AssignableUser[]>([])
  const selectedUserIds = selectedUsers.map((u) => u._id)
  const [selectedEmails, setSelectedEmails] = useState<AssignableUser[]>([])
  const selectedEmailIds = selectedEmails.map((u) => u._id)

  const [selectedPotentialIdeas, setSelectedPotentialIdeas] = useState<OpenAPI.Schemas["Idea"][]>([])
  const selectedPotentialIdeaIds = selectedPotentialIdeas.map((i) => i._id)

  // Any user or idea can be inspected, this inspection then shows the matrix of users/ideas who are currently draft assigned, and that are already assigned as assessors
  const [inspectedAssessor, setInspectedAssessor] = useState<AssignableUser | null>(null)
  const inspectedAssessorAssignments = useMemo(() => {
    if (!inspectedAssessor) {
      return null
    }
    if (inspectedAssessor.isEmailInvitee) {
      return localEmailAssignments[inspectedAssessor.email]?.ideas
    }
    return localUserAssignments[inspectedAssessor._id]?.ideas
  }, [inspectedAssessor, localEmailAssignments, localUserAssignments])

  const [inspectedIdea, setInspectedIdea] = useState<OpenAPI.Schemas["Idea"] | null>(null)
  // Reverse map of the local assignments for just the selected idea to show which users/emails are assigned
  const inspectedIdeaAssignments = useMemo(() => {
    if (!inspectedIdea) {
      return null
    }
    const assignments: LocalAssignments = {}
    for (const [userId, assignment] of Object.entries(localUserAssignments || {})) {
      if (assignment.ideas[inspectedIdea._id]) {
        assignments[userId] = {
          ...assignment,
          ideas: {
            [inspectedIdea._id]: assignment.ideas[inspectedIdea._id],
          },
        }
      }
    }
    for (const [email, assignment] of Object.entries(localEmailAssignments || {})) {
      if (assignment.ideas[inspectedIdea._id]) {
        assignments[email] = {
          ...assignment,
          ideas: {
            [inspectedIdea._id]: assignment.ideas[inspectedIdea._id],
          },
        }
      }
    }
    return assignments
  }, [inspectedIdea, localEmailAssignments, localUserAssignments])

  // Starting here are the api queries to fetch assessor & idea data
  // First is a query to fetch assessors with pagination and search
  const [assessorSearchParams, setAssessorSearchParams] = useState({
    query: "",
    page: 1,
    includePotential: true,
  })
  const throttleAssessments = useThrottle(api.challenges.getAssessmentDashboard, 400, [])
  const assessmentDashboardQuery = useQuery({
    queryKey: ["challenges", challenge?._id, "assessors", "dashboard", assessorSearchParams],
    queryFn: asQuery(throttleAssessments, {
      params: [challenge?._id, assessorSearchParams],
    })!,
    placeholderData: (data) => data,
  })

  // Secondly here ideas are fetched with the challenge filters applied from the redux reducer
  const ideaFilterQuery = useQuery({
    queryKey: ["challenges", challenge?._id, "ideas", "filters"],
    queryFn: asQuery(api.challenges.getIdeaFilters, {
      params: [challenge?._id],
    }),
  })
  const [potentialIdeaSearchSettings, setPotentialIdeaSearchSettings] = useState({
    query: "",
    page: 1,
    limit: 15,
  })
  const fetchPotentialIdeas = useThrottle(api.challenges.getIdeas, 400, [])
  const ideasQuery = useQuery({
    queryKey: ["challenges", challenge?._id, "ideas", potentialIdeaSearchSettings, challengesReducer],
    queryFn: asQuery(fetchPotentialIdeas, {
      params: [
        challenge?._id,
        {
          ...potentialIdeaSearchSettings,
          tags: tagFilter,
          filter,
          sort,
          sortDirection,
          labels,
          groupFilter,
          orgFilter,
          fieldChoices,
          adminHasCommented,
          projectBoardFilter,
          projectBoardLanesFilter,
          impactFilter,
          contentType: "assessment",
        },
      ],
    })!,
    placeholderData: (data) => data,
  })

  // We also construct a map of all ideas that are assessable for easy lookup in the inspect modals
  // Because of pagination we need to make sure that the idea data is always accessible from either the query or the local draft assignments. Otherwise changing a page might make data unavailable during inspection.
  const assessableIdeasById = useMemo(() => {
    const byId =
      assessmentDashboardQuery?.data?.ideas?.reduce(
        (obj, item) => {
          obj[item._id] = item
          return obj
        },
        {} as { [key: string]: OpenAPI.Schemas["Idea"] },
      ) || {}

    Object.entries(localUserAssignments).forEach(([userId, assignment]) => {
      Object.entries(assignment.ideas).forEach(([ideaId, ideaAssignment]) => {
        byId[ideaId] = ideaAssignment.idea
      })
    })
    Object.entries(localEmailAssignments).forEach(([userId, assignment]) => {
      Object.entries(assignment.ideas).forEach(([ideaId, ideaAssignment]) => {
        byId[ideaId] = ideaAssignment.idea
      })
    })

    return byId
  }, [assessmentDashboardQuery?.data?.ideas, localUserAssignments, localEmailAssignments])

  const addPotentialAssessors = useCallback(
    (userIds: string[], emails: string[]) => {
      api.challenges.addPotentialAssessors(
        challenge?._id,
        userIds,
        emails,
        () => {
          api.queryClient.invalidateQueries({
            queryKey: ["challenges", challenge?._id, "assessors", "dashboard"],
          })
        },
        () => {},
      )
    },
    [challenge?._id],
  )

  const removePotentialAssessors = useCallback(
    (userIds: string[], emails: string[]) => {
      api.challenges.removePotentialAssessors(
        challenge?._id,
        userIds,
        emails,
        () => {
          api.queryClient.invalidateQueries({
            queryKey: ["challenges", challenge?._id, "assessors", "dashboard"],
          })
        },
        () => {},
      )
    },
    [challenge?._id],
  )

  const searchAssessors = useCallback(
    (queryTerm: string, success: (users: OpenAPI.Schemas["User"][]) => void, fail) => {
      api.search.challengeAssessors(
        challenge?._id,
        queryTerm,
        ({ users }) => {
          success(users)
        },
        fail,
      )
    },
    [challenge?._id],
  )

  const unassignAssessor = useCallback(
    (ideaId: string, assessorId: string, assessmentRoundId: string) => {
      const round = assessmentRoundMap[assessmentRoundId]
      util
        .confirm(
          `Unassign assessor from assessment`,
          `Are you sure you want to unassign this assessor from ${round?.name}? Their existing assessment will be deleted. This action cannot be undone.`,
        )
        .then(() => {
          api.challenges.unassignAssessors(
            challenge._id,
            [ideaId],
            [assessorId],
            assessmentRoundId,
            () => {
              api.queryClient.invalidateQueries({
                queryKey: ["challenges", challenge?._id, "assessors", "dashboard"],
              })
              api.queryClient.invalidateQueries({
                queryKey: ["challenges", challenge?._id, "ideas"],
              })
              if (inspectedAssessor) {
                setInspectedAssessor((prev) => ({
                  ...prev,
                  assignments: {
                    ...prev.assignments,
                    [ideaId]: {
                      assessmentRoundIds:
                        prev.assignments?.[ideaId]?.assessmentRoundIds.filter((id) => id !== assessmentRoundId) || [],
                    },
                  },
                }))
              }
              if (inspectedIdea) {
                setInspectedIdea((prev) => {
                  return {
                    ...prev,
                    assessments: prev.assessments.filter((a) => {
                      return !(a.user === assessorId && (a.assessmentRoundId || "legacy") === assessmentRoundId)
                    }),
                  }
                })
              }
            },
            (err) => {
              toast.error(err.message)
            },
          )
        })
        .catch(() => {})
    },
    [challenge?._id, assessmentRoundMap, inspectedAssessor, inspectedIdea],
  )

  const assignLocalUsersAndEmailsToRound = useCallback(
    (roundId: string) => {
      setLocalUserAssignments((prev) => {
        const newState = { ...prev }
        for (const assessor of selectedUsers) {
          newState[assessor._id] = {
            user: assessor,
            ideas: {
              ...(newState[assessor._id]?.ideas || {}),
            },
          }

          for (const idea of selectedPotentialIdeas) {
            newState[assessor._id].ideas[idea._id] = {
              idea: idea,
              assessmentRoundIds: [...(newState[assessor._id].ideas[idea._id]?.assessmentRoundIds || [])],
            }
            if (!newState[assessor._id].ideas[idea._id].assessmentRoundIds.includes(roundId)) {
              newState[assessor._id].ideas[idea._id].assessmentRoundIds = [
                ...newState[assessor._id].ideas[idea._id].assessmentRoundIds,
                roundId,
              ]
            }
          }
        }
        return newState
      })
      setLocalEmailAssignments((prev) => {
        const newState = { ...prev }
        for (const email of selectedEmails) {
          newState[email.email] = {
            user: email,
            ideas: {
              ...(newState[email.email]?.ideas || {}),
            },
          }

          for (const idea of selectedPotentialIdeas) {
            newState[email.email].ideas[idea._id] = {
              idea: idea,
              assessmentRoundIds: [...(newState[email.email].ideas[idea._id]?.assessmentRoundIds || [])],
            }
            if (!newState[email.email].ideas[idea._id].assessmentRoundIds.includes(roundId)) {
              newState[email.email].ideas[idea._id].assessmentRoundIds = [
                ...newState[email.email].ideas[idea._id].assessmentRoundIds,
                roundId,
              ]
            }
          }
        }
        return newState
      })
      setSelectedUsers([])
      setSelectedEmails([])
      setSelectedPotentialIdeas([])
    },
    [selectedUsers, selectedEmails, selectedPotentialIdeas],
  )

  return (
    <Container fluid>
      <Prompt
        when={localUserAssignmentList.length > 0 || localEmailAssignmentList.length > 0}
        message="You have unsaved draft assignments. Are you sure you want to leave? This will discard your unsaved changes."
        shouldBlock={(e) => {
          return e.currentLocation.pathname !== e.nextLocation.pathname
        }}
      />
      <HandledIdeaPreview ideaId={previewIdea} closeModal={() => setPreviewIdea(null)} />
      <ChallengeIdeaFilters
        challenge={challenge}
        popularTags={ideaFilterQuery.data?.popularTags || []}
        allLabels={ideaFilterQuery.data?.allLabels || []}
        availableGroups={ideaFilterQuery.data?.availableGroups ?? []}
        availableOrganisations={ideaFilterQuery.data?.availableOrganisations || []}
        availableSortParameters={ideaFilterQuery.data?.availableSortParameters || {}}
        availableFilterParameters={ideaFilterQuery.data?.availableFilterParameters || {}}
        allProjectAssignees={ideaFilterQuery.data?.allProjectAssignees || []}
        availableImpacts={ideaFilterQuery.data?.availableImpacts || []}
      />
      <AssessmentReviewModal
        localUserAssignments={localUserAssignments}
        localEmailAssignments={localEmailAssignments}
        open={reviewOpen}
        setOpen={setReviewOpen}
        challenge={challenge}
        onSave={() => {
          setLocalUserAssignments({})
          setLocalEmailAssignments({})
        }}
      />
      <UserInspectModal
        inspectedAssessor={inspectedAssessor}
        setInspectedAssessor={setInspectedAssessor}
        localAssignments={inspectedAssessorAssignments}
        setLocalAssignments={inspectedAssessor?.isEmailInvitee ? setLocalEmailAssignments : setLocalUserAssignments}
        assessableIdeasById={assessableIdeasById}
        unassignAssessor={unassignAssessor}
        assessmentRoundMap={assessmentRoundMap}
      />
      <IdeaInspectModal
        emailInvites={assessmentDashboardQuery.data?.emails}
        inspectedIdea={inspectedIdea}
        setInspectedIdea={setInspectedIdea}
        localAssignments={inspectedIdeaAssignments}
        setLocalUserAssignments={setLocalUserAssignments}
        setLocalEmailAssignments={setLocalEmailAssignments}
        unassignAssessor={unassignAssessor}
        assessmentRoundMap={assessmentRoundMap}
      />

      <div
        style={{
          position: "absolute",
          marginTop: -55,
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          alignSelf: "flex-end",
          alignItems: "center",
          gap: 8,
          zIndex: 10,
        }}
      >
        <Button.Group compact>
          <Popup
            trigger={
              assessmentRounds?.length > 1 ? (
                <Dropdown
                  icon={null}
                  disabled={
                    (selectedUsers.length === 0 && selectedEmails.length === 0) || selectedPotentialIdeas.length === 0
                  }
                  trigger={
                    <Button
                      secondary={
                        (selectedUsers.length !== 0 || selectedEmails.length !== 0) &&
                        selectedPotentialIdeas.length !== 0
                      }
                      content={
                        selectedUsers.length === 0 && selectedEmails.length === 0 && selectedPotentialIdeas.length === 0
                          ? util.pluralise(totalAssignmentCount, "assignment", "assignments")
                          : `Add ${util.pluralise(selectedUsers.length + selectedEmails.length, "user", "users")} & ${util.pluralise(selectedPotentialIdeaIds.length, t("generic.idea"), t("generic.ideas"))} to..`
                      }
                    />
                  }
                  direction="left"
                >
                  <DropdownMenu>
                    {assessmentRounds?.map((round) => (
                      <DropdownItem
                        key={round.id}
                        text={round.name}
                        onClick={() => assignLocalUsersAndEmailsToRound(round.id)}
                      />
                    ))}
                  </DropdownMenu>
                </Dropdown>
              ) : (
                <Button
                  onClick={() => assignLocalUsersAndEmailsToRound(assessmentRounds[0]?.id)}
                  secondary={
                    (selectedUsers.length !== 0 || selectedEmails.length !== 0) && selectedPotentialIdeas.length !== 0
                  }
                  disabled={
                    (selectedUsers.length === 0 && selectedEmails.length === 0) || selectedPotentialIdeas.length === 0
                  }
                  content={
                    selectedUsers.length === 0 && selectedEmails.length === 0 && selectedPotentialIdeas.length === 0
                      ? util.pluralise(totalAssignmentCount, "assignment", "assignments")
                      : `Add ${util.pluralise(selectedUsers.length + selectedEmails.length, "user", "users")} & ${util.pluralise(selectedPotentialIdeaIds.length, t("generic.idea"), t("generic.ideas"))}`
                  }
                />
              )
            }
            content={`Locally assign assessors to ${t("generic.ideas")}. This will not be saved until you click "Save assignments".`}
          />
          <Button
            disabled={localUserAssignmentList.length === 0 && localEmailAssignmentList.length === 0}
            icon="trash"
            onClick={() => {
              util
                .confirm(
                  "Clear all draft assignments",
                  `Are you sure you want to clear all draft assignments? There are currently ${util.pluralise(localUserAssignmentList.length + localEmailAssignmentList.length, "user", "users")} with unsaved draft assignments.`,
                )
                .then(() => {
                  setLocalUserAssignments({})
                  setLocalEmailAssignments({})
                })
            }}
          />
        </Button.Group>

        <Button
          compact
          disabled={localUserAssignmentList.length === 0 && localEmailAssignmentList.length === 0}
          content="Review & assign"
          primary={localUserAssignmentList.length > 0 || localEmailAssignmentList.length > 0}
          onClick={() => {
            setReviewOpen(true)
          }}
        />
      </div>
      <Grid columns={2} style={{ marginBottom: 10 }}>
        <Grid.Row divided>
          <Grid.Column>
            <GridHeader>
              <Header>Assessor audience</Header>
            </GridHeader>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 12 }}>
                <div>
                  <UserChooser
                    clearOnComplete
                    trigger={<Button secondary size="small" compact content="Add assessors" />}
                    onComplete={(newPotentialAssessors) => {
                      const newUserIds: string[] = []
                      const newEmails: string[] = []
                      for (const newPotentialAssessor of newPotentialAssessors) {
                        const addingCollection = newPotentialAssessor.isEmailInvitee ? newEmails : newUserIds
                        if (!addingCollection.includes(newPotentialAssessor._id)) {
                          addingCollection.push(newPotentialAssessor._id)
                        }
                      }
                      addPotentialAssessors(newUserIds, newEmails)
                    }}
                    searchFunction={searchAssessors}
                    forType="assessor"
                    forId={challenge?._id}
                    afterContent={() =>
                      !challenge?.visibility?.organisations?.includes(user.organisation) ? (
                        <AssessorAudienceWarning />
                      ) : null
                    }
                  />
                  <Button.Group size="small" compact>
                    <Button
                      content="Select all on page"
                      onClick={() => {
                        setSelectedUsers((prevState) => {
                          const newState = [...prevState]
                          const ids = newState.map((u) => u._id)
                          for (const user of assessmentDashboardQuery?.data?.users || []) {
                            if (!ids.includes(user._id)) {
                              newState.push(user as AssignableUser)
                            }
                          }

                          return newState
                        })

                        setSelectedEmails((prevState) => {
                          const newState = [...prevState]
                          const ids = newState.map((email) => email.email)
                          for (const email of assessmentDashboardQuery?.data?.emails || []) {
                            if (!ids.includes(email.email)) {
                              newState.push(email as AssignableUser)
                            }
                          }

                          return newState
                        })
                      }}
                    />
                    {selectedUsers?.length > 0 || selectedEmails?.length > 0 ? (
                      <Button
                        content="Deselect all"
                        onClick={() => {
                          setSelectedUsers([])
                          setSelectedEmails([])
                        }}
                      />
                    ) : null}
                  </Button.Group>
                </div>
              </div>

              <TabPane>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                  }}
                >
                  <Input
                    icon="search"
                    value={assessorSearchParams.query}
                    onChange={(e) => {
                      setAssessorSearchParams((prevState) => ({
                        ...prevState,
                        page: 1,
                        query: e.target.value,
                      }))
                    }}
                    loading={assessmentDashboardQuery.isFetching}
                    placeholder={`Search assessors...`}
                  />
                  {assessmentDashboardQuery.data?.previousPageAvailable ||
                  assessmentDashboardQuery.data?.nextPageAvailable ? (
                    <div>
                      <Pagination
                        onPageChange={(_e, { activePage }) =>
                          setAssessorSearchParams((prev) => ({ ...prev, page: activePage as number }))
                        }
                        activePage={assessorSearchParams.page}
                        totalPages={Math.ceil((assessmentDashboardQuery?.data.total || 0) / 25)}
                        ellipsisItem={null}
                        firstItem={null}
                        lastItem={null}
                      />
                    </div>
                  ) : null}
                </div>
                <Table basic="very" fixed compact>
                  <Table.Body>
                    {assessmentDashboardQuery.data?.users?.map((item) => {
                      return (
                        <AssessorRow
                          key={item._id}
                          item={item as AssignableUser}
                          localAssignments={localUserAssignments[item._id]?.ideas}
                          onInspect={setInspectedAssessor}
                          isSelected={selectedUserIds.includes(item._id)}
                          emailAssignments={assessmentDashboardQuery.data?.emails}
                          onSelect={() => {
                            setSelectedUsers((prevState) => {
                              const ids = prevState.map((u) => u._id)
                              if (ids.includes(item._id)) {
                                return prevState.filter((u) => u._id !== item._id)
                              } else {
                                return [...prevState, item as AssignableUser]
                              }
                            })
                          }}
                          removeAssessor={(userId) => {
                            setSelectedUsers((prevState) => prevState.filter((u) => u._id !== item._id))
                            removePotentialAssessors([userId], [])
                          }}
                        />
                      )
                    })}
                  </Table.Body>
                </Table>
                {assessmentDashboardQuery.data?.emails?.length > 0 ? (
                  <>
                    <Header as="h4">Invited via email</Header>
                    <Table basic="very" fixed compact>
                      <Table.Body>
                        {assessmentDashboardQuery.data?.emails?.map((item) => {
                          return (
                            <AssessorRow
                              key={item._id}
                              item={item as AssignableUser}
                              localAssignments={localEmailAssignments[item._id]?.ideas}
                              onInspect={setInspectedAssessor}
                              isSelected={selectedEmailIds.includes(item._id)}
                              onSelect={() => {
                                setSelectedEmails((prevState) => {
                                  const ids = prevState.map((u) => u._id)
                                  if (ids.includes(item._id)) {
                                    return prevState.filter((u) => u._id !== item._id)
                                  } else {
                                    return [...prevState, item as AssignableUser]
                                  }
                                })
                              }}
                              removeAssessor={(email) => {
                                setSelectedEmails((prevState) => prevState.filter((u) => u._id !== item._id))
                                removePotentialAssessors([], [email])
                              }}
                            />
                          )
                        })}
                      </Table.Body>
                    </Table>
                  </>
                ) : null}
              </TabPane>
            </div>
          </Grid.Column>
          <Grid.Column>
            <GridHeader>
              <Header>Available {t("generic.ideas")}</Header>
            </GridHeader>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 12 }}>
                <div>
                  <Button
                    secondary
                    size="small"
                    compact
                    content={
                      (filtersVisible ? `Hide filters` : `Show filters`) +
                      `${numberOfFiltersNotDefault ? ` (${numberOfFiltersNotDefault})` : ""}`
                    }
                    onClick={() => {
                      updateFilters({ filtersVisible: !filtersVisible })
                    }}
                  />
                  <Button.Group compact size="small">
                    <Button
                      content="Select all on page"
                      onClick={() => {
                        setSelectedPotentialIdeas((prevState) => {
                          const newState = [...prevState]
                          const ids = newState.map((u) => u._id)
                          for (const idea of ideasQuery?.data?.ideas || []) {
                            if (!ids.includes(idea._id)) {
                              newState.push(idea)
                            }
                          }
                          return newState
                        })
                      }}
                    />
                    {selectedPotentialIdeas?.length > 0 ? (
                      <Button content="Deselect all" onClick={() => setSelectedPotentialIdeas([])} />
                    ) : null}
                  </Button.Group>
                </div>
              </div>

              <TabPane>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <Input
                    icon="search"
                    value={potentialIdeaSearchSettings.query}
                    onChange={(e) => {
                      setPotentialIdeaSearchSettings((prevState) => ({
                        ...prevState,
                        page: 1,
                        query: e.target.value,
                      }))
                    }}
                    loading={ideasQuery.isFetching}
                    placeholder={`Search ${t("generic.ideas")}...`}
                  />
                  {ideasQuery.data?.previousPageAvailable || ideasQuery.data?.nextPageAvailable ? (
                    <div>
                      <Pagination
                        onPageChange={(_e, { activePage }) =>
                          setPotentialIdeaSearchSettings((prev) => ({ ...prev, page: activePage as number }))
                        }
                        activePage={potentialIdeaSearchSettings.page}
                        totalPages={Math.ceil((ideasQuery?.data.total || 0) / potentialIdeaSearchSettings.limit)}
                        ellipsisItem={null}
                        firstItem={null}
                        lastItem={null}
                      />
                    </div>
                  ) : null}
                </div>
                {ideasQuery?.data?.ideas?.length ? (
                  <Table basic="very" fixed compact>
                    <Table.Body>
                      {ideasQuery?.data?.ideas?.map((idea) => (
                        <AssessmentIdeaRow
                          key={idea._id}
                          idea={idea}
                          emailInvites={assessmentDashboardQuery.data?.emails}
                          localUserAssignments={localUserAssignments}
                          localEmailAssignments={localEmailAssignments}
                          onInspect={setInspectedIdea}
                          onPreview={() => setPreviewIdea(idea._id)}
                          isSelected={selectedPotentialIdeaIds.includes(idea._id)}
                          onSelect={() => {
                            setSelectedPotentialIdeas((prevState) => {
                              const ids = prevState.map((i) => i._id)
                              if (ids.includes(idea._id)) {
                                return prevState.filter((i) => i._id !== idea._id)
                              } else {
                                return [...prevState, idea]
                              }
                            })
                          }}
                        />
                      ))}
                    </Table.Body>
                  </Table>
                ) : null}
              </TabPane>
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Container>
  )
}

export default AssignmentDashboard
